import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import authService from "../../../../services/authService";
import type { Step1Data } from "./validations/step1Validation";
import { validateStep1 } from "./validations/step1Validation";
import type { Step2Data } from "./validations/step2Validation";
import { validateStep2 } from "./validations/step2Validation";
import type { Step3Data } from "./validations/step3Validation";
import { validateStep3 } from "./validations/step3Validation";

export const useCompletarCadastro = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Step 1 Data
  const [step1Data, setStep1Data] = useState<Step1Data>({
    nomeCompleto: "",
    nomeSocial: "",
    cpf: "",
    genero: "",
    etnia: "",
    dataNascimento: "",
    paisNascimento: "Brasil",
    ufNascimento: "",
    municipioNascimento: "",
    rg: "",
    orgaoEmissor: "",
    ufOrgaoEmissor: "",
    email: "",
    telefoneCelular: "",
    enderecoResidencial: "",
    cep: "",
    bairro: "",
    pais: "Brasil",
    municipio: "",
    uf: "",
    vinculoEspecifico: "",
  });
  const [step1Errors, setStep1Errors] = useState<Partial<Step1Data>>({});

  // Buscar dados do usuário ao carregar
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          setStep1Data((prev) => ({
            ...prev,
            nomeCompleto: user.nome || "",
            email: user.email || "",
            cpf: user.cpf || "",
            telefoneCelular: user.telefone || "",
          }));
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);
      }
    };

    loadUserData();
  }, []);

  // Step 2 Data
  const [step2Data, setStep2Data] = useState<Step2Data>({
    maiorTitulacao: "",
    nomeCursoTitulacao: "",
    instituicaoTitulacao: "",
    mesTitulacao: "",
    anoTitulacao: "",
    nomeCursoAndamento: "",
    periodoMatricula: "",
    grandeAreaConhecimento: "",
    subAreaConhecimento: "",
    anoMatriculaAtual: "",
    municipioCurso: "",
    ufCurso: "",
  });
  const [step2Errors, setStep2Errors] = useState<Partial<Step2Data>>({});

  // Step 3 Data
  const [step3Data, setStep3Data] = useState<Step3Data>({
    documentoRGCPF: null,
    comprovanteFormacao: null,
    comprovanteResidencia: null,
  });
  const [step3FileNames, setStep3FileNames] = useState({
    documentoRGCPF: "",
    comprovanteFormacao: "",
    comprovanteResidencia: "",
  });
  const [step3Errors, setStep3Errors] = useState<
    Partial<Record<keyof Step3Data, string>>
  >({});

  // Handlers
  const handleStep1Change = (name: keyof Step1Data, value: string) => {
    setStep1Data((prev) => ({ ...prev, [name]: value }));
    if (step1Errors[name]) {
      setStep1Errors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleStep2Change = (name: keyof Step2Data, value: string) => {
    setStep2Data((prev) => ({ ...prev, [name]: value }));
    if (step2Errors[name]) {
      setStep2Errors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleStep3Change = (name: keyof Step3Data, file: File | null) => {
    setStep3Data((prev) => ({ ...prev, [name]: file }));
    setStep3FileNames((prev) => ({ ...prev, [name]: file?.name || "" }));
    if (step3Errors[name]) {
      setStep3Errors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validations
  const validateCurrentStep1 = (): boolean => {
    const errors = validateStep1(step1Data);
    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCurrentStep2 = (): boolean => {
    const errors = validateStep2(step2Data);
    setStep2Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCurrentStep3 = (): boolean => {
    const errors = validateStep3(step3Data);
    setStep3Errors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    let isValid = true;

    if (currentStep === 1) {
      isValid = validateCurrentStep1();
    } else if (currentStep === 2) {
      isValid = validateCurrentStep2();
    }

    if (isValid && currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep3()) {
      return;
    }

    setIsLoading(true);

    try {
      // Preparar FormData para envio
      const formData = new FormData();

      // Step 1
      Object.entries(step1Data).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Step 2
      Object.entries(step2Data).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Step 3 - Arquivos
      if (step3Data.documentoRGCPF)
        formData.append("documentoRGCPF", step3Data.documentoRGCPF);
      if (step3Data.comprovanteFormacao)
        formData.append("comprovanteFormacao", step3Data.comprovanteFormacao);
      if (step3Data.comprovanteResidencia)
        formData.append(
          "comprovanteResidencia",
          step3Data.comprovanteResidencia
        );

      const apiBase =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";
      const response = await fetch(
        `${apiBase.replace(/\/$/, "")}/api/v1/proponentes/completar-cadastro`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            data.errors?.join(", ") ||
            "Erro ao completar cadastro"
        );
      }

      toast.success("Cadastro completado com sucesso! ✅");

      // Redirecionar para home
      setTimeout(() => {
        window.location.href = "/home";
      }, 1000); // Delay para mostrar o toast antes de redirecionar
    } catch (error) {
      console.error("Erro ao finalizar cadastro:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erro ao finalizar cadastro. Tente novamente.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    currentStep,
    isLoading,

    // Step 1
    step1Data,
    step1Errors,
    handleStep1Change,

    // Step 2
    step2Data,
    step2Errors,
    handleStep2Change,

    // Step 3
    step3Data,
    step3FileNames,
    step3Errors,
    handleStep3Change,

    // Navigation
    handleNext,
    handlePrevious,
    handleSubmit,
  };
};
